import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { cn } from "@/lib/utils/cn";
import styles from "./Button.module.css";

/**
 * Props for the Button component
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant of the button */
  variant?: "default" | "primary" | "secondary" | "ghost" | "outline" | "link";
  /** Size of the button */
  size?: "sm" | "md" | "lg" | "icon";
  /**
   * If true, renders children as-is instead of wrapping in a button element.
   * Useful for rendering buttons as links or other elements.
   */
  asChild?: boolean;
}

/**
 * Button component with multiple variants and sizes.
 *
 * Built on Radix UI Slot primitive for maximum flexibility.
 * Supports all native button HTML attributes.
 *
 * @example
 * ```tsx
 * // Default button
 * <Button>Click me</Button>
 *
 * // Secondary variant
 * <Button variant="secondary" size="lg">
 *   Large Secondary
 * </Button>
 *
 * // As a link
 * <Button asChild>
 *   <Link href="/about">About</Link>
 * </Button>
 *
 * // Ghost button
 * <Button variant="ghost" onClick={handleClick}>
 *   Ghost Button
 * </Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = "default", 
    size = "md", 
    asChild = false, 
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          styles.button,
          styles[variant],
          styles[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button };